import { gql } from "@apollo/client";
import { Divider, IconButton, List, ListItem, ListItemText, Typography } from "@mui/material";
import FormDialog from "controls/FormDialog";
import { useActionFragment } from "hooks/useAction";
import useData from "hooks/useData";
import { Delete, Pencil } from "mdi-material-ui";
import React, { useEffect, useState } from "react";

import GameSchedulesForm, { GameSchedulesFormFragment } from "./Form";
import ParticipantInput from "./ParticipantInput";
import { PracticeGameSchedulesForm, PracticeGameSchedulesFormFragment } from "./PracticeForm";

GameSchedulesEditDialog.useButtonProps = () => {
  return {
    icon: <Pencil />,
    content: "Edit",
  };
};

export default function GameSchedulesEditDialog({ gameScheduleIds, onSubmit, onClose }) {
  const gameSchedulesUpdate = useActionFragment("gameSchedulesUpdate");
  const [participants, setParticipants] = useState(null);

  const [data, dataMeta] = useData(
    gql`
      query CourtsEditDialog($gameScheduleIds: [ID!]!) {
        gameSchedules(ids: $gameScheduleIds) {
          id
          ...GameSchedulesFormFragment
          ...PracticeGameSchedulesFormFragment
          practiceGameSpectators {
            id
            personId
            person {
              id
              readminName
            }
          }
        }
      }
      ${GameSchedulesFormFragment}
      ${PracticeGameSchedulesFormFragment}
    `,
    { gameScheduleIds },
  );

  const isPractice = data?.gameSchedules?.every((g) => g.practice);

  useEffect(() => {
    if (!data || participants !== null) return;
    const existing = data.gameSchedules[0]?.practiceGameSpectators ?? [];
    setParticipants(existing.map((s) => ({ person_id: s.personId, name: s.person.readminName })));
  }, [data]);

  return (
    <FormDialog
      loading={dataMeta.loading}
      header="Edit Game Schedule"
      onClose={onClose}
      onSubmit={async (formData) => {
        const input = { gameScheduleIds, ...formData };
        if (isPractice && participants !== null) {
          input.people = participants.map((p) => ({ personId: p.person_id, role: "spectator" }));
        }
        await gameSchedulesUpdate({ input });
        await onSubmit?.();
      }}
    >
      {isPractice ? (
        <PracticeGameSchedulesForm gameSchedules={data?.gameSchedules} />
      ) : (
        <GameSchedulesForm gameSchedules={data?.gameSchedules} />
      )}

      {isPractice && participants !== null && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" component="h3" sx={{ mt: 0, mb: 1 }}>
            Spectators
          </Typography>
          {participants.length > 0 && (
            <List dense>
              {participants.map((p) => (
                <ListItem
                  key={p.person_id}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => setParticipants((prev) => prev.filter((s) => s.person_id !== p.person_id))}
                    >
                      <Delete />
                    </IconButton>
                  }
                >
                  <ListItemText primary={p.name} />
                </ListItem>
              ))}
            </List>
          )}
          <ParticipantInput
            onAddParticipant={(p) => setParticipants((prev) => [...prev, p])}
            participants={participants}
          />
        </>
      )}
    </FormDialog>
  );
}
